import {
  PropsWithChildren,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';

type ContextType<T extends string> = {
  settingName: T;
  setSettingName: (name: T) => void;
  useSettingIsDefault: (settingName: T) => boolean | null;
  settingsList: readonly T[];
};
// keep the underlying context wide (any) so Provider and hook can be generic
const selectedSettingNameContexts = createContext<ContextType<any> | null>(
  null
);

type Props<T extends string> = {
  defaultName: T;
  useSettingIsDefault: (settingName: T) => boolean | null;
  settingsList: readonly T[];
};

export const HpaSelectedSettingNameProvider = <T extends string>({
  defaultName,
  useSettingIsDefault,
  settingsList,
  ...props
}: PropsWithChildren<Props<T>>) => {
  const [settingName, setSettingName] = useState<T>(defaultName);

  return (
    <selectedSettingNameContexts.Provider
      value={useMemo(
        () => ({
          settingName,
          setSettingName,
          useSettingIsDefault,
          settingsList,
        }),
        [settingName, settingsList, useSettingIsDefault]
      )}
      {...props}
    />
  );
};

export const useHpaSelectedSettingName = <
  T extends string
>(): ContextType<T> => {
  const context = useContext(selectedSettingNameContexts);
  if (!context) {
    throw new Error(
      'useHpaSelectedSettingName must be used within a HpaSelectedSettingNameProvider'
    );
  }
  return context;
};
